import { useCallback, useEffect, useRef, useState } from 'react';
import { useRealtimeFeed } from 'services/client/realtime';
import type { DarkPoolRow } from 'constants/payloads/realtime';
import { mergeDarkPoolRows as merge } from 'components/DarkPoolTable/mergeDarkPoolRows';

const FLUSH_MS = 500; // D23: flush to React state at most every 500ms

/**
 * Dark pool realtime rows (spec §11.3, Phase 1). Snapshot seeds the list;
 * deltas are buffered and flushed to state at most every 500ms (D23) so a burst
 * of prints doesn't thrash React. Returns the same row shape the baseline query
 * yields, so the table is untouched.
 */
export function useDarkPoolRealtime(enabled: boolean): { rows: DarkPoolRow[]; status: string } {
  const [rows, setRows] = useState<DarkPoolRow[]>([]);
  const buffer = useRef<DarkPoolRow[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flush = useCallback(() => {
    timer.current = null;
    if (!buffer.current.length) return;
    const batch = buffer.current;
    buffer.current = [];
    setRows((prev) => merge(prev, batch));
  }, []);

  const schedule = useCallback(() => {
    if (timer.current) return;
    timer.current = setTimeout(flush, FLUSH_MS);
  }, [flush]);

  const { status } = useRealtimeFeed({
    feed: 'darkpool',
    enabled,
    onSnapshot: (body) => {
      const snap = (body as { rows?: DarkPoolRow[] }).rows ?? [];
      buffer.current = [];
      setRows(merge([], snap));
    },
    onDelta: (delta) => {
      buffer.current.push(...(delta.rows as DarkPoolRow[]));
      schedule();
    },
    onReset: () => {
      buffer.current = [];
      setRows([]);
    },
  });

  useEffect(() => {
    if (!enabled) {
      buffer.current = [];
      setRows([]);
    }
    return () => {
      if (timer.current) clearTimeout(timer.current);
      timer.current = null;
    };
  }, [enabled]);

  // Clear held rows on fallback so the 11-min retry can't re-render rows frozen at the outage
  // start as live; fallback renders Apollo, a retry-success re-snapshots fresh (§17.12 R4).
  useEffect(() => {
    if (status === 'fallback') {
      buffer.current = [];
      setRows([]);
    }
  }, [status]);

  return { rows, status };
}
